import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatCount } from "../Helpers/utils";
import UpdateProfileModal from "./ProfileUpdateModal";
import { useGlobalState } from "../contexts/GlobalStateContext";
import { createPortal } from "react-dom";


async function fetchFollowState(followingId) {
  try {
    const response = await fetch(
      `/php/get_follow_state.php?following_id=${followingId}`
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching follow state:", error);
    return { isFollowed: false, noOfFollowers: 0, noOfFollowing: 0 };
  }
}

async function fetchFollowers(userId, page = 1) {
  try {
    const response = await fetch(
      `/php/fetch_followers.php?user_id=${userId}&page=${page}`
    );
    const data = await response.json();

    if (data.status === "success") {
      const followers = data.followers || [];
      const hasMore = followers.length === 30; // assume each page loads max 30
      return {
        followers,
        hasMore,
      };
    } else {
      console.error("Failed to fetch followers:", data.message);
      return { followers: [], hasMore: false };
    }
  } catch (error) {
    console.error("Error fetching followers:", error);
    return { followers: [], hasMore: false };
  }
}

async function fetchFollowing(userId, page = 1) {
  try {
    const response = await fetch(
      `/php/fetch_following.php?user_id=${userId}&page=${page}`
    );
    const data = await response.json();
    if (data.status === "success") {
      const following = data.following || [];
      return {
        following,
        hasMore: following.length === 30,
      };
    } else {
      console.error("Failed to fetch following:", data.message);
      return { following: [], hasMore: false };
    }
  } catch (error) {
    console.error("Error fetching following:", error);
    return { following: [], hasMore: false };
  }
}

export default function FollowButton({ user }) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(
    Number(user.noOfFollowers)
  );
  const [followingCount, setFollowingCount] = useState(
    Number(user.noOfFollowing)
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const { globalState, setShowLogins, logOut, resetGlobalState } = useGlobalState();

  useEffect(() => {
    if (!globalState.isAuthenticated) return;

    const loadInitialFollowState = async () => {
      const data = await fetchFollowState(user.user_id);
      setIsFollowing(data.isFollowed);
    };
    loadInitialFollowState();
  }, [user.user_id]);

  const toggleFollow = async () => {
    if (!globalState.isAuthenticated) {
      setShowLogins(true);
      return;
    }
    try {
      const action = isFollowing ? "unfollow" : "follow";
      const response = await fetch("/php/toggle_follow.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.user_id, action }),
      });

      const data = await response.json();
      if (data.success) {
        setIsFollowing(!isFollowing);
        setFollowersCount(
          (prev) => Number(prev) + (action === "follow" ? 1 : -1)
        );
      } else if (data.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        console.error("Toggle follow failed:", data.error);
      }
    } catch (error) {
      console.error("Error in toggleFollow:", error);
    }
  };

  const openModal = async (type) => {
    setModalTitle(type === "followers" ? "Followers" : "Following");
    setModalOpen(true);
    setLoading(true);
    setPage(1);
    setHasMore(true);

    if (type === "followers") {
      const { followers, hasMore } = await fetchFollowers(user.user_id, 1);
      setModalData(followers);
      setHasMore(hasMore);
    } else {
      const { following, hasMore } = await fetchFollowing(user.user_id, 1);
      setModalData(following);
      setHasMore(hasMore);
    }

    setLoading(false);
  };

  const closeModal = () => setModalOpen(false);

  const loadMoreModalData = async (e) => {
    const element = e.target;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 50;

    if (!hasMore || loading || !nearBottom) return;

    setLoading(true);
    const nextPage = page + 1;
    setPage(nextPage);

    if (modalTitle === "Followers") {
      const { followers, hasMore: moreFollowers } = await fetchFollowers(
        user.user_id,
        nextPage
      );
      setModalData((prev) => [...prev, ...followers]);
      setHasMore(moreFollowers);
    } else {
      const { following, hasMore: moreFollowing } = await fetchFollowing(
        user.user_id,
        nextPage
      );
      setModalData((prev) => [...prev, ...following]);
      setHasMore(moreFollowing);
    }

    setLoading(false);
  };


  return (
    <>
      {/* Portal for UpdateProfileModal */}
      {isUpdateModalOpen &&
        createPortal(
          <UpdateProfileModal
            user={globalState.user}
            onClose={() => setIsUpdateModalOpen(false)}
          />,
          document.body
        )}

      {/* Portal for Followers/Following Modal */}
      {modalOpen &&
        createPortal(
          <div id="universal-modal" className="modal" style={{ display: "block" }}>
            <div className="UM-content">
              <div id="comments-header">
                <span id="modal-title">{modalTitle}</span>
                <span className="close" onClick={closeModal}>
                  &times;
                </span>
              </div>
              <div
                id="UM-section"
                className="modal-body"
                style={{ backgroundColor: "white", overflowY: "auto" }}
                onScroll={loadMoreModalData}
              >
                {loading && page === 1 ? (
                  <p>Loading...</p>
                ) : modalData.length === 0 ? (
                  <p>No {modalTitle.toLowerCase()} yet.</p>
                ) : (
                  modalData.map((item) => {
                    const id =
                      modalTitle === "Followers"
                        ? item.follower_id
                        : item.following_id;
                    const username =
                      modalTitle === "Followers"
                        ? item.follower_username
                        : item.following_username;
                    const fullName =
                      modalTitle === "Followers"
                        ? item.follower_fullName
                        : item.following_fullName;

                    return (
                      <div key={id} className="like-item">
                        <Link
                          to={`/@${encodeURIComponent(username)}`}
                          style={{
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={`/profile_pics_icons/${id}.jpg`}
                            alt={`${fullName}'s profile picture`}
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "50%",
                              marginRight: "10px",
                            }}
                          />
                          <span>{fullName}</span>
                        </Link>
                      </div>
                    );
                  })
                )}
                {loading && page > 1 && <p>Loading more...</p>}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Buttons */}
      {/* Buttons */}
      {globalState.user && user.self && !globalState.viewAsPublic ? (
        <div
          style={{
            backgroundColor: user.user_id == globalState.user?.user_id
              ? globalState.user?.theme
              : user.theme || "#333",
            color: user.user_id == globalState.user?.user_id
              ? globalState.user?.text_bg
              : user.text_bg || "white",
          }}
          className="bottom-left"
          onClick={() => setIsUpdateModalOpen(true)}
        >
          Update
        </div>
      ) : (
        <button
          onClick={toggleFollow}
          className="bottom-left"
          style={{
            bottom: "9px",
            left: "3px",
            // Use optional chaining (?.) here as well to prevent the "null" crash
            backgroundColor: globalState.user?.user_id === user.user_id
              ? globalState.user?.theme
              : user.theme || "#333",
            color: globalState.user?.user_id === user.user_id
              ? globalState.user?.text_bg
              : user.text_bg || "white",
            padding: "5px 12px",
            borderRadius: "15px",
            opacity: 0.7,
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {isFollowing ? "Unfollow" : "Follow"}
        </button>
      )}

      {/* Footer */}
      <div className="footer">
        <button id="followers" onClick={() => openModal("followers")}>
          {formatCount(followersCount)}{" "}
          {parseInt(followersCount) === 1 ? "Follower" : "Followers"}
        </button>{" "}
        |{" "}
        <button id="following" onClick={() => openModal("following")}>
          Following {formatCount(followingCount)}
        </button>
      </div>
    </>
  );
}
